/*
 * The flow API client.
 *
 * One rule governs this file: it transports, it does not decide. The server
 * hands back the next step and this turns a response into something the UI can
 * switch on — it never inspects a step id, never picks a branch, never
 * substitutes a step of its own. Every routing decision in the product lives in
 * packages/flow/src/canonical.ts, and a client that quietly disagrees with it
 * is the failure mode the whole flow package exists to prevent.
 *
 * See apps/web/src/app/api/v1 for the endpoints this speaks to.
 */

import type { Answers, StaticOption, Step, TenantChrome } from '@blooq/flow'

import { apiBase } from './tenant'

/** Mirrors StepView in apps/web/src/lib/runtime.ts. */
export interface StepView {
  sessionId: string
  step: Step
  options: StaticOption[]
  answers: Answers
  errors?: Record<string, string>
  tenant: TenantChrome
  /** Appointment ref, FAQ answer, payment details — whatever the step needs. */
  result?: Record<string, unknown>
}

/**
 * The ways a call can fail, named for what the UI must DO about each.
 *
 * Not for what went wrong — a 410 and a 500 are both "an error" to a logger and
 * completely different to a customer. The three the server distinguishes are
 * distinguished here for exactly that reason, and the mapping is the one
 * documented on the step route.
 */
export type FlowFailure =
  /** The session aged out, or ran against an older flow version. Start again. */
  | { kind: 'expired' }
  /** The business stopped being served mid-conversation. Retrying cannot help. */
  | { kind: 'not_serving' }
  /** No such business, or none we will name. */
  | { kind: 'not_found' }
  /** Too fast. The only failure worth asking the customer to wait out. */
  | { kind: 'rate_limited' }
  /** The network, or something we did not anticipate. Retrying might help. */
  | { kind: 'unavailable' }

export type FlowResult = { ok: true; view: StepView } | { ok: false; failure: FlowFailure }

function url(path: string): string {
  return `${apiBase}/api/v1${path}`
}

/** HTTP status to the thing the customer needs to happen next. */
function failureFor(status: number): FlowFailure {
  if (status === 410) return { kind: 'expired' }
  if (status === 403) return { kind: 'not_serving' }
  if (status === 404) return { kind: 'not_found' }
  if (status === 429) return { kind: 'rate_limited' }
  return { kind: 'unavailable' }
}

async function call(path: string, init: RequestInit): Promise<FlowResult> {
  let response: Response
  try {
    response = await fetch(url(path), init)
  } catch {
    // Offline, DNS, a captive portal. Indistinguishable from here and all
    // recoverable by trying again, which is what `unavailable` means.
    return { ok: false, failure: { kind: 'unavailable' } }
  }

  if (!response.ok) return { ok: false, failure: failureFor(response.status) }

  try {
    return { ok: true, view: (await response.json()) as StepView }
  } catch {
    return { ok: false, failure: { kind: 'unavailable' } }
  }
}

/** Open a conversation. Returns the first step, already resolved for this tenant. */
export function startSession(slug: string): Promise<FlowResult> {
  return call('/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug }),
  })
}

/**
 * Answer the current step.
 *
 * A validation failure is NOT a failure here: the server returns 200 with the
 * same step carrying `errors`, because to a renderer an invalid answer is a
 * state to draw rather than a transport problem.
 */
export function sendStep(sessionId: string, payload: Record<string, unknown>): Promise<FlowResult> {
  return call(`/session/${sessionId}/step`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

/** Go back one step. The flow decides where that lands; this only asks. */
export function goBack(sessionId: string): Promise<FlowResult> {
  return sendStep(sessionId, { __back: true })
}

/**
 * The payment screenshot.
 *
 * Posted to its own endpoint rather than through a step, because a step payload
 * is JSON and an image is not. It answers with a full StepView, so the caller
 * swaps its whole view instead of keeping a local "did the upload work" flag
 * that can disagree with the server.
 */
export async function uploadPaymentProof(
  sessionId: string,
  file: Blob,
  filename = 'proof.jpg',
): Promise<{ ok: true; view: StepView } | { ok: false; message: string }> {
  const body = new FormData()
  body.set('proof', file, filename)

  let response: Response
  try {
    response = await fetch(url(`/session/${sessionId}/payment-proof`), { method: 'POST', body })
  } catch {
    return { ok: false, message: 'Upload failed. Check your connection and try again.' }
  }

  let data: Record<string, unknown>
  try {
    data = (await response.json()) as Record<string, unknown>
  } catch {
    return { ok: false, message: 'Upload failed. Please try again.' }
  }

  if (!response.ok) {
    /*
     * The server's own message wins where it sent one — it knows why it
     * refused. The fallbacks name the two refusals a customer can act on and
     * say what to do about each, because "upload failed" in front of someone
     * holding a booking they have already paid for is the worst possible copy.
     */
    const message =
      typeof data.message === 'string'
        ? data.message
        : data.error === 'storage_unavailable'
          ? 'We could not save that just now. You can pay in person instead.'
          : data.error === 'rate_limited'
            ? 'Too many attempts. Wait a moment and try once more.'
            : 'That file was not accepted. Try a PNG or JPEG screenshot.'
    return { ok: false, message }
  }

  return { ok: true, view: data as unknown as StepView }
}
